// 创建一个文件，暂且命名为 context.tsx，导出 createContext() 的返回值
import React, { createContext, useState } from 'react';

import { apiAuth } from './utils/feishu-api-auth';
import { isDev, isLocal } from '@/config/index';
import type {
  IAuthClient,
  IUserInfo,
} from '@/feishu/typing';
import { message } from 'antd';
import { useQueryParams } from '@/hooks/useQueryParams';
import console from './utils/debug';
import { encodeFeishuState } from './utils/state';

export const feishuConfig = {
  authClient: {} as IAuthClient,
};

interface IContext {
  userInfo: IUserInfo;
  initialized: boolean;
  logout: () => void;
}

export const useFeishu = (): IContext => {
  const feishuContext = React.useContext(FeishuContext);
  return feishuContext;
};

export const FeishuContext = createContext<IContext>({
  userInfo: {
    name: '',
  },
  initialized: false,
  logout: () => undefined,
});

const defaultLoginUrl = '/';
interface IFeishuProviderProps {
  children: React.ReactNode
  authClient: IAuthClient
}
export const FeishuProvider: React.FC<IFeishuProviderProps> = (props) => {
  const { children, authClient } = props;

  const {
    loginUrl = defaultLoginUrl,
    checkIfLogin,
    fetchAppId,
    fetchCallback,
  } = authClient || {};

  feishuConfig.authClient = authClient;

  const [userInfo, setUserInfo] = useState<IUserInfo>({});
  const [initialized, setInitialized] = useState<boolean>(false);
  const handleInfo = (isInitialized: boolean, info: IUserInfo) => {
    setInitialized(isInitialized);
    setUserInfo(info);
  };

  const onLogin = () => {
    console.info('main isDev', isDev, location.pathname);
    checkIfLogin().then(async res => {
      const { code, data } = res || {};
      console.info('checkInfLogin', JSON.stringify(res), 'location.href', location.href);
      // 规避接口返回 code 值不同系统表现不一致的问题
      if (code == null || !data) {
        // 接口返回 html 的情况
        message.error('登录失败！');
        handleInfo(true, {});
        return;
      }
      const { user_info: userInfo, is_login: isLogin } = data;
      /**
       * 刷新页面后的判断，
       * 若登录了，则直接获取用户信息。
       * 若未登录，则走登录流程。
       */
      if (isLogin) {
        handleInfo(true, userInfo);
        return;
      } else {
        console.info('测试当前的 pathname 是什么 location.pathname !== loginUrl',
          location.pathname !== loginUrl, 'location.pathname', location.pathname);
        if (location.pathname !== loginUrl) {
          const { pathname, search } = location;
          console.info('Current location is', location, JSON.stringify(location));
          const oldUrl = `${pathname}`;
          console.info('oldUrl is', oldUrl);
          const queryStr = search.startsWith('?') ? search.slice(1) : search;
          const url = `${loginUrl}?redirect=${encodeURIComponent(oldUrl)}&queryStr=${encodeURIComponent(queryStr)}`;
          console.info('redirect url is', url);
          location.href = url;
          // 用下面的方式不跳转？
          // his.push(url);
          return;
        }
      }

      const h5sdk = window.h5sdk;
      const queryParams: { code?: string; state?: string; redirect?: string; queryStr?: string } = useQueryParams();
      const { code: authCode, state } = queryParams;
      // 非飞书客户端环境下，使用浏览器网页登录。
      if (!h5sdk) {
        console.info('!h5sdk queryParams', queryParams);
        if (authCode) {
          const userInfo = await fetchCallback({ code: authCode });
          console.info('!h5sdk getUserInfo succeed', userInfo);
          if (!userInfo) return;
          handleInfo(true, userInfo);
          // 跳转由 pages/login.tsx 中的 useEffect 用 history.replace 完成（SPA 内部跳转）
          return;
        }

        /**
         * 飞书浏览器网页登录：https://open.feishu.cn/document/uYjL24iN/ukTO4UjL5kDO14SO5gTN#9f534005
         */
        fetchAppId().then(res => {
          console.info('fetchAppId res', res);
          const APPID = res.appid;
          // redirect_uri 固定为登录页地址，动态跳转信息放入 state
          const REDIRECT_URI = encodeURIComponent(`${location.origin}${loginUrl}`);
          // 将 redirect 和 queryStr 信息编码到 state 参数中，飞书会原样返回
          const stateObj = { redirect: queryParams.redirect, queryStr: queryParams.queryStr };
          const STATE = encodeFeishuState(stateObj);
          const loginPath = 'https://open.feishu.cn/open-apis/authen/v1/index';
          const url = `${loginPath}?redirect_uri=${REDIRECT_URI}&app_id=${APPID}&state=${STATE}`;
          console.info('重定向 url', url);
          // 跳转到飞书登录认证页面，回调 URL 示例：
          // https://wts.cla-dev.mmtdev.com/
          //   ?code=8AZhAb5Ezy244fCdaG3H46GcA5445KA5
          //   &state={"redirect":"/fill-report/demand-report",
          //           "queryStr":"lang=zh-CN&open_in_browser=true"}
          location.href = url;
        });
      } else {
        apiAuth((userInfo, lang) => {
          console.info('apiAuth userInfo', userInfo);
          console.info('apiAuth lang', lang);
          if (!userInfo?.name) {
            window.location.reload();
            return;
          }
          handleInfo(true, userInfo);
        });
      }
    }).catch(err => {
      // 接口 502 的情况。
      message.error('登录失败！Error is:', err);
      console.error('Error is', err);
      handleInfo(true, {});
    });
  };
  React.useEffect(() => {
    onLogin();
  }, []);

  const logout = async () => {
    // 酌情使用
    // await onLogout();
    // window.location.reload();
  };

  return (
    <FeishuContext.Provider
      value={{
        userInfo,
        initialized,
        logout,
      }}
    >
      {children}
    </FeishuContext.Provider>
  );
};
